import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTest } from '../../contexts/TestContext';
import { questionSets } from '../../config/questions';
import { emotionalAreas } from '../../config/areas';
import ProgressBar from './ProgressBar';
import './Quiz.css';

const Quiz = () => {
  const { areaId } = useParams();
  const navigate = useNavigate();
  const { setSelectedArea, updateAnswer, answers, calculateResults } = useTest();
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [direction, setDirection] = useState(1); // 1 para avanzar, -1 para retroceder
  const [areaTitleInfo, setAreaTitleInfo] = useState({ title: '', color: '' });
  
  useEffect(() => {
    if (!areaId || !questionSets[areaId]) {
      navigate('/area-selection');
      return;
    }
    
    setSelectedArea(areaId);
    setQuestions(questionSets[areaId]);
    
    // Obtener información del área para el título
    const areaInfo = emotionalAreas.find(area => area.id === areaId);
    if (areaInfo) {
      setAreaTitleInfo({
        title: areaInfo.name,
        color: areaInfo.color
      });
    }
    
    // Deshabilitar TODOS los popups (incluyendo WhatsApp) durante el test en dispositivos móviles
    // Utilizamos una técnica más robusta que solo enviar eventos a GTM
    const isMobile = window.innerWidth <= 768;
    
    const disablePopups = () => {
      // 1. Enviamos evento a GTM para deshabilitar
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'disable_whatsapp_popup',
          'disable_popup': true
        });
      }
      
      // 2. Si estamos en móvil, agregamos un estilo global para ocultar cualquier popup
      if (isMobile) {
        const styleTag = document.createElement('style');
        styleTag.id = 'disable-popups-style';
        styleTag.innerHTML = `
          /* Ocultar cualquier popup de WhatsApp y otros que puedan aparecer durante el test */
          div[class*="popup"], div[class*="whatsapp"], iframe[src*="whatsapp"], 
          div[class*="chat"], div[id*="whatsapp"], div[id*="popup"], 
          div[id*="chat"], div[class*="Popup"], div[class*="Chat"] { 
            display: none !important; 
            opacity: 0 !important; 
            visibility: hidden !important; 
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(styleTag);
      }
    };
    
    const enablePopups = () => {
      // 1. Restaurar configuración en GTM
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'disable_whatsapp_popup',
          'disable_popup': false
        });
      }
      
      // 2. Si estamos en móvil, eliminar el estilo global
      if (isMobile) {
        const styleTag = document.getElementById('disable-popups-style');
        if (styleTag) {
          styleTag.remove();
        }
      }
    };
    
    // Aplicar cambios inmediatamente
    disablePopups();
    
    // Cuando se desmonte el componente, volvemos a habilitar los popups
    return () => {
      enablePopups();
    };
  }, [areaId, navigate, setSelectedArea]);
  
  const handleOptionSelect = (value) => {
    // Guardar la respuesta en el contexto
    if (questions[currentQuestionIndex]) {
      updateAnswer(questions[currentQuestionIndex].id, value);
    }
    
    // Si es la última pregunta, calcular resultados y navegar a la página de resultados
    if (currentQuestionIndex === questions.length - 1) {
      calculateResults();
      navigate('/results');
    } else {
      // Avanzar a la siguiente pregunta
      setDirection(1);
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
      
      // Scroll hacia la pregunta en dispositivos móviles
      if (window.innerWidth <= 768) {
        // Desplazamiento inmediato al inicio del contenedor
        window.scrollTo({ top: 0, behavior: 'auto' });
        
        setTimeout(() => {
          // Desplazamiento suave al contenedor de pregunta
          const quizHeader = document.querySelector('.quiz-header');
          if (quizHeader) {
            quizHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 50);
      }
    }
  };
  
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setDirection(-1);
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
    } else {
      navigate('/area-selection');
    }
  };
  
  const handleChangeArea = () => {
    navigate('/area-selection');
  };
  
  // Variantes para la animación de las preguntas
  const questionVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 500 : -500,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction > 0 ? -500 : 500,
      opacity: 0
    })
  };
  
  // Si no hay preguntas, mostrar un indicador de carga
  if (questions.length === 0) {
    return <div className="loading">Cargando preguntas...</div>;
  }
  
  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  
  return (
    <div className="quiz-container">
      <motion.div 
        className="quiz-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 style={{ color: areaTitleInfo.color }}>
          Nivel {areaTitleInfo.title.toLowerCase()}
        </h2>
        
        <ProgressBar progress={progress} color={areaTitleInfo.color} />
        
        <div className="question-counter">
          Pregunta {currentQuestionIndex + 1} de {questions.length}
        </div>
      </motion.div>
      
      <div className="question-container">
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div 
            key={currentQuestionIndex}
            custom={direction}
            variants={questionVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5 }}
            className="question-card"
          >
            <h3 className="question-text">{currentQuestion.question}</h3>
            
            <div className="options-container">
              {currentQuestion.options.map((option) => (
                <motion.button
                  key={option}
                  className={`option-button ${answers[currentQuestion.id] === option ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(option)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ backgroundColor: '#f8f9fa' }} // Ensure the initial state is the same for all options
                >
                  {option}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      
      <div className="quiz-navigation">
        <button 
          className="nav-button previous"
          onClick={handlePrevious}
        >
          {currentQuestionIndex === 0 ? 'Cambiar área' : 'Anterior'}
        </button>
        
        {currentQuestionIndex > 0 && (
          <button 
            className="nav-button change-area"
            onClick={handleChangeArea}
          >
            Cambiar área
          </button>
        )}
      </div>
    </div>
  );
};

export default Quiz;
